import { distance, overlaps, type Circle, type Vec2 } from './geometry.ts';

/** Shrinks a region's raw axis-span budget inward so a label never licks the
 *  boundary it lives near. */
const MARGIN = 0.85;

/** Region identity by sorted set membership, e.g. ['a'], ['a','b']. */
export type RegionKey = string[];
/** Worklet-safe: called from the UI thread wherever a region's key needs
 *  turning into the same string its budget/label bookkeeping keys on
 *  (previously reimplemented inline as `key.join('+')` at every call
 *  site that lived in a worklet — this is that debt paid off). */
export const regionId = (key: RegionKey): string => {
  'worklet';
  return [...key].sort().join('+');
};

export interface NamedCircle {
  id: string;
  circle: Circle;
}

export interface Region {
  key: RegionKey;
  /** natural home for the region's words */
  anchor: Vec2;
  /** Max box a label may occupy at the anchor (already margined): height is
   *  measured at the anchor's exact axis offset and is only achievable by a
   *  NARROW label — width and height are not simultaneously achievable,
   *  since the budget box's corners can poke past the bounding curve even
   *  though its center line fits, so Task 4's auto-fit must size text
   *  conservatively rather than treating this as a true inscribed rectangle. */
  budget: { width: number; height: number };
}

/** Half the chord height of a circle of radius r at axis offset x from its
 *  own center; 0 once x is outside the circle. */
function halfHeight(r: number, x: number): number {
  'worklet';
  return Math.sqrt(Math.max(0, r * r - x * x));
}

/** Unit vector from a to b along the center line; an arbitrary but stable
 *  choice when the centers coincide (d===0), where every direction is
 *  equally valid by radial symmetry. */
function axisUnit(a: Vec2, b: Vec2, d: number): Vec2 {
  'worklet';
  if (d === 0) return { x: 1, y: 0 };
  return { x: (b.x - a.x) / d, y: (b.y - a.y) / d };
}

interface Bound {
  /** the bounding circle's radius */
  r: number;
  /** that circle's center, measured as an axis offset from the shared origin (a.center) */
  centerOffset: number;
}

/** Builds a region from an axis span [start, end] (offsets from a.center
 *  along u): the anchor is the span midpoint, and the budget is the
 *  margined box that fits inside every bounding circle at that anchor. A
 *  degenerate (zero-or-negative-length) span still yields the region —
 *  it exists, it just has no room — with a zero, never negative, budget. */
function spanRegion(
  key: RegionKey,
  start: number,
  end: number,
  origin: Vec2,
  u: Vec2,
  bounds: Bound[],
): Region {
  'worklet';
  const anchorOffset = (start + end) / 2;
  const anchor: Vec2 = {
    x: origin.x + u.x * anchorOffset,
    y: origin.y + u.y * anchorOffset,
  };
  const length = end - start;
  if (length <= 0) {
    return { key, anchor, budget: { width: 0, height: 0 } };
  }
  const height =
    2 *
    MARGIN *
    Math.min(
      ...bounds.map(({ r, centerOffset }) =>
        halfHeight(r, anchorOffset - centerOffset),
      ),
    );
  const width = MARGIN * length;
  return { key, anchor, budget: { width, height } };
}

/** The exact 2-circle path: closed-form axis-span, unchanged since the
 *  first kiss. Kept verbatim (not folded into the n-generic hybrid below)
 *  because it is exact and ~100x cheaper than the general case — see
 *  docs/plans/2026-08-22-the-third-scoop.md's n-regions research report. */
function regionsForPair(a: NamedCircle, b: NamedCircle): Region[] {
  'worklet';
  const rA = a.circle.radius;
  const rB = b.circle.radius;
  const d = distance(a.circle.center, b.circle.center);
  const u = axisUnit(a.circle.center, b.circle.center, d);
  const origin = a.circle.center;

  const regions: Region[] = [];

  // only-a exists iff a is not fully contained in b.
  if (d + rA > rB) {
    regions.push(
      spanRegion([a.id], -rA, Math.min(rA, d - rB), origin, u, [
        { r: rA, centerOffset: 0 },
      ]),
    );
  }

  // only-b exists iff b is not fully contained in a. Mirrors only-a exactly,
  // but measured from b's own center along the reversed axis.
  if (d + rB > rA) {
    regions.push(
      spanRegion(
        [b.id],
        -rB,
        Math.min(rB, d - rA),
        b.circle.center,
        { x: -u.x, y: -u.y },
        [{ r: rB, centerOffset: 0 }],
      ),
    );
  }

  if (overlaps(a.circle, b.circle)) {
    // General intersection span. The plan's literal [d - rB, rA] holds only
    // in the typical partial-overlap case; when one circle is fully
    // contained in the other, min/max is required or the anchor lands past
    // the contained circle's own boundary. This generalization reduces to
    // the plan's formula exactly on its own worked numbers (r=10, d=15).
    const start = Math.max(-rA, d - rB);
    const end = Math.min(rA, d + rB);
    regions.push(
      spanRegion([a.id, b.id], start, end, origin, u, [
        { r: rA, centerOffset: 0 },
        { r: rB, centerOffset: d },
      ]),
    );
  }

  return regions;
}

// ---------------------------------------------------------------------
// The n-generic hybrid (n === 1 or n >= 3; n === 2 always takes the exact
// path above). Ported from the n-regions spike (branch feat/n-regions,
// commit 6d60ddf) per docs/plans/2026-08-22-the-third-scoop.md's report.
//
// Two anchor/budget strategies, chosen per region by whether it has any
// EXCLUDED circles:
//
//  - Fully-included regions (no exclusions: a lone circle's own interior,
//    or the pairwise/triple LENS) are convex — an intersection of disks.
//    Anchor: the centroid of the pairwise boundary-intersection vertices
//    that face inward (closed-form circle-circle intersection). Budget:
//    a single `regionDepth` evaluation at that point. No search.
//
//  - Exclusion-bearing regions ("only-a", "a+b minus c") are generally
//    NON-convex once a disk is subtracted, so there is no clean closed
//    form. These use a short, fixed-iteration compass hill-climb on the
//    same depth function, extended with excluded-circle constraints. This
//    is the same shape of approximation `spanRegion` above already
//    accepts (documented there as inexact, never a true inscribed
//    rectangle) — a generalization of that acceptance, not a new one.
//
// EXISTENCE IS NOT THE SEARCH'S BUSINESS. The hill-climb decides where a
// label sits; it never decides whether a region is there. It used to: a
// region existed iff the climb happened to find depth > 0, and the
// climb's eight fixed axis-aligned directions made that an
// orientation-dependent opinion — three r=10 circles overlapping by 0.5
// lost two of their three lenses, and WHICH two cycled as the figure was
// rotated on the canvas. A lens the renderer drew and `regionAt` would
// happily tap into had no region to tap: a dead editor.
//
// So existence now comes from `witnessesByMask` below: a fixed,
// orientation-free set of candidate points derived from the arrangement's
// own geometry, each classified by the SAME exact membership predicate
// `regionAt` uses. Region K exists iff some witness lies strictly inside
// exactly K's circles and strictly outside the rest. Rotating the figure
// rotates every witness with it, so the answers cannot move.
// ---------------------------------------------------------------------

function centroidOf(points: Vec2[]): Vec2 {
  'worklet';
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / points.length, y: y / points.length };
}

/** Margin to the nearest violated-or-tight constraint: positive means `p`
 *  is a valid witness, and its value is the radius of the largest circle
 *  centered at `p` that stays inside every included disk and outside
 *  every excluded one. This IS the existence test (depth <= 0 => empty)
 *  and the budget (2 * MARGIN * depth) in one function. */
function regionDepth(
  p: Vec2,
  included: NamedCircle[],
  excluded: NamedCircle[],
): number {
  'worklet';
  let m = Infinity;
  for (const s of included) {
    const v = s.circle.radius - distance(p, s.circle.center);
    if (v < m) m = v;
  }
  for (const s of excluded) {
    const v = distance(p, s.circle.center) - s.circle.radius;
    if (v < m) m = v;
  }
  return m;
}

/** Closed-form circle-circle intersection points, or null if none (no
 *  real intersection, including the concentric d=0 case). */
function circleIntersections(a: Circle, b: Circle): [Vec2, Vec2] | null {
  'worklet';
  const d = distance(a.center, b.center);
  if (d === 0) return null;
  const aOffset = (a.radius ** 2 - b.radius ** 2 + d * d) / (2 * d);
  const h2 = a.radius ** 2 - aOffset ** 2;
  if (h2 < 0) return null;
  const h = Math.sqrt(h2);
  const ux = (b.center.x - a.center.x) / d;
  const uy = (b.center.y - a.center.y) / d;
  const px = a.center.x + aOffset * ux;
  const py = a.center.y + aOffset * uy;
  const perpX = -uy;
  const perpY = ux;
  return [
    { x: px + h * perpX, y: py + h * perpY },
    { x: px - h * perpX, y: py - h * perpY },
  ];
}

/** Closed-form anchor for a fully-included (no exclusions) intersection
 *  of >= 1 circles: for a lone circle, its own center. For >= 2, the
 *  centroid of the pairwise boundary vertices that face INWARD (that lie
 *  inside every other included circle too). Those vertices are corners of
 *  the lens itself, and a lens is convex with no straight edges, so the
 *  centroid of two or more distinct corners is strictly interior — the
 *  anchor is inside the region by construction, not by luck.
 *
 *  Vertices that are NOT inward are corners of some other region and are
 *  dropped outright. Averaging one in (the old `inward ?? pts[0]`
 *  fallback) dragged the anchor toward a point outside the lens: on
 *  Ferruccio's probe — a=(0,0,10), b=(15,0,10), c=(7.5,0.9,3) — it cost
 *  the a+b+c label 63% of its earned depth, shrinking text for nothing.
 *
 *  RETURNS NULL when no vertex qualifies, and the caller uses the witness
 *  point instead (issue 210). This used to fall back to the centroid of
 *  the CENTERS, with a comment calling that "exactly right" — and it was,
 *  for the concentric case, and for no other. No boundary crossing among
 *  the included circles means they are NESTED, so the region is the
 *  innermost disk and its deepest point is that disk's own centre; the
 *  centroid of every centre has no reason to equal it unless all the
 *  centres coincide, in which case the two are the same point anyway.
 *
 *  The old fallback survived because `regionsForMany` rejected it at
 *  depth <= 0 — which only fires when the centroid lands OUTSIDE the
 *  region. The commoner case is inside-but-shallow, where nothing fired
 *  and the label simply got a smaller budget than the region could carry:
 *  27% smaller for a scoop-sized circle inside two canvas-sized ones, 88%
 *  for a three-deep nest, measured across 45 app-reachable arrangements
 *  in regions.test.ts. `witnessesByMask` considers every circle's centre
 *  and keeps the deepest, so for exactly these arrangements the witness
 *  IS the innermost centre — the right answer was already in hand. */
function lensAnchor(included: NamedCircle[]): Vec2 | null {
  'worklet';
  const n = included.length;
  if (n === 1) return included[0]!.circle.center;
  const vertices: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const pts = circleIntersections(included[i]!.circle, included[j]!.circle);
      if (!pts) continue;
      for (const pt of pts) {
        let inward = true;
        for (let k = 0; k < n && inward; k++) {
          if (k === i || k === j) continue;
          const o = included[k]!.circle;
          if (distance(pt, o.center) > o.radius + 1e-9) inward = false;
        }
        if (inward) vertices.push(pt);
      }
    }
  }
  if (vertices.length === 0) return null;
  return centroidOf(vertices);
}

/** The compass rose, hoisted: eight unit directions, allocated once for
 *  the module rather than 2^n - 1 times per frame. Module-level consts
 *  are captured by worklets, so this stays UI-thread safe. */
const HUNT_DIRECTIONS: readonly number[] = [
  1, 0, -1, 0, 0, 1, 0, -1, 0.7071, 0.7071, -0.7071, 0.7071, 0.7071, -0.7071,
  -0.7071, -0.7071,
];

/**
 * Short fixed-iteration compass hill-climb on `regionDepth`, refining an
 * anchor for the exclusion-bearing (non-convex) regions. Bounded cost:
 * 6 iterations * 8 directions * (included + excluded) distance
 * evaluations.
 *
 * `seed` is the deepest witness already known to lie INSIDE this region,
 * so the climb starts valid and, because it only ever steps to a strictly
 * greater depth, it stays valid: the returned point always has depth > 0.
 * That invariant is why this function no longer has to nudge itself away
 * from the nearest excluded circle to escape a bad start — it is handed a
 * good one. Whether the region exists was settled before we got here.
 */
function huntDeepestPoint(
  included: NamedCircle[],
  excluded: NamedCircle[],
  seed: Vec2,
): Vec2 {
  'worklet';
  let p = seed;
  let step = 0;
  for (const s of included) {
    if (s.circle.radius > step) step = s.circle.radius;
  }
  step *= 0.5;
  for (let iter = 0; iter < 6; iter++) {
    let best = p;
    let bestVal = regionDepth(p, included, excluded);
    for (let k = 0; k < HUNT_DIRECTIONS.length; k += 2) {
      const cand = {
        x: p.x + HUNT_DIRECTIONS[k]! * step,
        y: p.y + HUNT_DIRECTIONS[k + 1]! * step,
      };
      const v = regionDepth(cand, included, excluded);
      if (v > bestVal) {
        bestVal = v;
        best = cand;
      }
    }
    p = best;
    step *= 0.55;
  }
  return p;
}

interface Witness {
  point: Vec2;
  depth: number;
}

/** How far off a boundary vertex a probe is pushed, as a fraction of the
 *  smaller of the two radii meeting there. A ladder, not one value: the
 *  widest step lands well clear of floating-point noise, the narrowest
 *  still fits a sliver a millionth of a radius thick. A face thinner than
 *  the smallest rung has no room for a label anyway — its budget would
 *  round to nothing. */
const VERTEX_PROBE_FRACTIONS: readonly number[] = [1e-2, 1e-4, 1e-6];

/** The four ways to leave a vertex: inside/outside circle i crossed with
 *  inside/outside circle j — one per face meeting there. */
const QUADRANT_SIGNS: readonly number[] = [1, 1, 1, -1, -1, 1, -1, -1];

/**
 * DEFINED BEFORE ITS CALLERS ON PURPOSE — do not move it back down.
 *
 * A plain function declaration hoists, so ordering would not matter in
 * ordinary JavaScript. These are worklets, and the worklets plugin rewrites
 * a `'worklet'` function declaration into a binding that does NOT hoist. A
 * worklet calling a worklet declared further down the file therefore reads
 * it while it is still in the temporal dead zone, and the app dies on boot
 * with "Cannot access 'sweepCenterLine' before initialization" — a blank
 * canvas, no venn, nothing.
 *
 * Node and vitest never see it, because neither applies the worklet
 * transform: the core suite stays green at 120 while the app refuses to
 * start. Found in the browser, 2026-08-22.
 */
/** Walks the line `origin + t * u`, collecting where it enters and leaves
 *  every circle, and offers the midpoint of each resulting cell as a
 *  candidate. `scratch` is reused across lines to keep the allocation
 *  count flat; it doubles as the "did any line run at all" flag. */
function sweepCenterLine(
  sets: NamedCircle[],
  origin: Vec2,
  u: Vec2,
  scratch: number[],
  consider: (point: Vec2) => void,
): void {
  'worklet';
  scratch.length = 0;
  for (const s of sets) {
    const c = s.circle;
    const fx = c.center.x - origin.x;
    const fy = c.center.y - origin.y;
    const along = fx * u.x + fy * u.y;
    const h2 = c.radius * c.radius - (fx * fx + fy * fy - along * along);
    if (h2 <= 0) continue;
    const h = Math.sqrt(h2);
    scratch.push(along - h, along + h);
  }
  scratch.sort((p, q) => p - q);
  for (let k = 0; k + 1 < scratch.length; k++) {
    const t = (scratch[k]! + scratch[k + 1]!) / 2;
    consider({ x: origin.x + u.x * t, y: origin.y + u.y * t });
  }
}

/**
 * The exact existence pass, and the seed supply for the search.
 *
 * Returns, per membership mask, the deepest candidate point found to lie
 * strictly inside exactly that mask's circles. A mask with no entry has
 * no region: not "the search failed to find one" — no point of the plane
 * classified into it.
 *
 * The candidates are the arrangement's own landmarks, so they rotate with
 * the figure and never depend on the canvas's axes:
 *
 *  1. Every circle's center. Catches a circle wholly inside others (a
 *     speck floating in a lens keeps its triple region) and any face
 *     whose only boundary is one whole circle.
 *  2. Four probes just off each pairwise boundary vertex, one into each
 *     of the four faces that meet there. Faces pinch out AT vertices, so
 *     every face with a corner is caught here however thin it is — this
 *     is what the eight-compass-points search kept missing on slim
 *     crescents.
 *  3. The midpoint of every cell along each center line, cut by all n
 *     circles. Center lines run through the thickest part of every
 *     pairwise lens, and they are the one family that reaches faces with
 *     NO corners at all: annuli and nested rings, whose boundaries are
 *     whole circles. When every center coincides the figure is radially
 *     symmetric about that point, so any direction cuts the same radii
 *     and the (1, 0) fallback is as geometric as a derived one.
 *
 * Classification is the exact predicate, shared with `regionAt`: signed
 * distance > 0 is in, < 0 is out, and the depth is the smallest absolute
 * signed distance. A candidate exactly on a boundary has depth 0 and is
 * discarded — it witnesses nothing.
 */
function witnessesByMask(sets: NamedCircle[]): Map<number, Witness> {
  'worklet';
  const n = sets.length;
  const best = new Map<number, Witness>();

  const consider = (point: Vec2): void => {
    'worklet';
    let mask = 0;
    let depth = Infinity;
    for (let i = 0; i < n; i++) {
      const c = sets[i]!.circle;
      const signed = c.radius - distance(point, c.center);
      if (signed > 0) mask |= 1 << i;
      const magnitude = signed < 0 ? -signed : signed;
      if (magnitude < depth) depth = magnitude;
    }
    if (mask === 0 || !(depth > 0)) return;
    const current = best.get(mask);
    if (!current || depth > current.depth) best.set(mask, { point, depth });
  };

  for (const s of sets) consider(s.circle.center);

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const a = sets[i]!.circle;
      const b = sets[j]!.circle;
      const pts = circleIntersections(a, b);
      if (!pts) continue;
      const scale = Math.min(a.radius, b.radius);
      for (const v of pts) {
        const ax = (v.x - a.center.x) / a.radius;
        const ay = (v.y - a.center.y) / a.radius;
        const bx = (v.x - b.center.x) / b.radius;
        const by = (v.y - b.center.y) / b.radius;
        for (let q = 0; q < QUADRANT_SIGNS.length; q += 2) {
          const sa = QUADRANT_SIGNS[q]!;
          const sb = QUADRANT_SIGNS[q + 1]!;
          const dx = sa * ax + sb * bx;
          const dy = sa * ay + sb * by;
          const len = Math.hypot(dx, dy);
          // Near-tangent circles have near-parallel normals, so one
          // diagonal collapses; that face is degenerate, not missed.
          if (len < 1e-9) continue;
          for (const fraction of VERTEX_PROBE_FRACTIONS) {
            const t = (fraction * scale) / len;
            consider({ x: v.x + dx * t, y: v.y + dy * t });
          }
        }
      }
    }
  }

  const offsets: number[] = [];
  let sweptAnyLine = false;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const from = sets[i]!.circle.center;
      const to = sets[j]!.circle.center;
      const d = distance(from, to);
      if (d === 0) continue;
      sweepCenterLine(sets, from, axisUnit(from, to, d), offsets, consider);
      sweptAnyLine = true;
    }
  }
  if (n > 0 && !sweptAnyLine) {
    // Every center coincides: the figure is radially symmetric about that
    // point, so one arbitrary ray cuts exactly the same radii as any
    // other and (1, 0) costs nothing in orientation-independence.
    sweepCenterLine(
      sets,
      sets[0]!.circle.center,
      { x: 1, y: 0 },
      offsets,
      consider,
    );
  }

  return best;
}

/** Every non-empty subset of `sets`, as (included, excluded) pairs. Up to
 *  2^n - 1 of them; fine for the app's real n (a handful of scoops). */
function regionsForMany(input: NamedCircle[]): Region[] {
  'worklet';
  // Canonical order first: anchors used to shift by up to 0.11 depending
  // on which order the caller happened to list the same three circles in,
  // because the centroid seed and the mask enumeration both read `sets`
  // positionally. Sorting by id here makes the whole pipeline a function
  // of the FIGURE, not of the argument list. (Region keys already sort.)
  const sets = [...input].sort((p, q) =>
    p.id < q.id ? -1 : p.id > q.id ? 1 : 0,
  );
  const witnesses = witnessesByMask(sets);
  const regions: Region[] = [];
  const n = sets.length;
  for (let mask = 1; mask < 1 << n; mask++) {
    const witness = witnesses.get(mask);
    // No witness, no region — decided, not searched for.
    if (!witness) continue;
    const included: NamedCircle[] = [];
    const excluded: NamedCircle[] = [];
    const key: string[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        included.push(sets[i]!);
        key.push(sets[i]!.id);
      } else {
        excluded.push(sets[i]!);
      }
    }
    let anchor: Vec2;
    if (excluded.length === 0) {
      // Convex: the closed form is exact, so the search stays out of it.
      // Null means it had no vertices to average — a nested arrangement,
      // whose deepest point is the innermost centre and therefore already
      // the witness (issue 210).
      const closed = lensAnchor(included);
      anchor = closed === null ? witness.point : closed;
      if (regionDepth(anchor, included, excluded) <= 0) anchor = witness.point;
    } else {
      anchor = huntDeepestPoint(included, excluded, witness.point);
    }
    // The hunt only steps to strictly greater depth from a positive seed,
    // and the closed form is checked above, so `d` is always > 0 here.
    const d = regionDepth(anchor, included, excluded);
    regions.push({
      key,
      anchor,
      budget: { width: d * 2 * MARGIN, height: d * 2 * MARGIN },
    });
  }
  return regions;
}

/** Regions for exactly two named circles: the exact closed-form path. */
export function regionsFor(a: NamedCircle, b: NamedCircle): Region[];
/** Regions for any number of named circles: the exact path at n===2,
 *  the closed-form/short-search hybrid otherwise. `regionsFor([])` is
 *  `[]` — an empty canvas has no regions, not one empty one — and the
 *  result never depends on the order the circles arrive in. */
export function regionsFor(sets: NamedCircle[]): Region[];
export function regionsFor(
  aOrSets: NamedCircle | NamedCircle[],
  b?: NamedCircle,
): Region[] {
  'worklet';
  if (Array.isArray(aOrSets)) {
    if (aOrSets.length === 2) {
      return regionsForPair(aOrSets[0]!, aOrSets[1]!);
    }
    return regionsForMany(aOrSets);
  }
  return regionsForPair(aOrSets, b!);
}

/**
 * The region a point falls in: every set whose circle contains it, sorted.
 *
 * Membership IS specificity — a point inside both circles belongs to the
 * lens, and the lens key is strictly longer than either exclusive key — so
 * "the most specific region wins" needs no ranking pass: collecting every
 * containing set already gives exactly one answer. Null when the point is
 * outside every circle, which is the canvas itself and not a region.
 *
 * The boundary counts as inside: a tap that lands on the rim of a scoop is
 * a tap on that scoop, not on nothing.
 */
export function regionAt(
  point: Vec2,
  sets: readonly NamedCircle[],
): RegionKey | null {
  'worklet';
  const members: string[] = [];
  for (const set of sets) {
    if (distance(point, set.circle.center) <= set.circle.radius) {
      members.push(set.id);
    }
  }
  if (members.length === 0) return null;
  return members.sort();
}
